import {
  getVisibleSettingsSections,
  ResolvedSettingsPage,
  SETTINGS_PAGES,
  SettingsPage,
  SettingsVisibilityContext,
} from 'src/features/settings/registry/settingsRegistry';

/** How many matching settings a result lists under its page, as the prototype does. */
export const MAX_SETTING_HITS = 3;

export interface SettingsSearchResult {
  page: ResolvedSettingsPage;
  /** The page's settings whose labels match, at most MAX_SETTING_HITS. */
  hits: string[];
}

/** Lowercase words separated by single spaces, so punctuation never decides a match. */
const toWords = (text: string) =>
  text
    .toLowerCase()
    .replace(/[^\p{L}\p{N}]+/gu, ' ')
    .trim();

/**
 * Whether the query starts at a word in `text`: "sso" finds "Google SSO" but
 * not "association", and "magic link" still finds "Magic links".
 */
const matchesWordStart = (text: string, needle: string) =>
  ` ${toWords(text)}`.includes(` ${needle}`);

/**
 * The pages a query finds, in sidebar order: a page matches on its own label,
 * its keywords, or any of its settings' labels, where the query has to start
 * at a word. Only pages the sidebar would show are searched, so a result is
 * always one the user can open.
 */
export const searchSettingsPages = ({
  query,
  ctx,
  clientTerm,
  pages = SETTINGS_PAGES,
}: {
  query: string;
  ctx: SettingsVisibilityContext;
  clientTerm: string;
  pages?: ReadonlyArray<SettingsPage>;
}): SettingsSearchResult[] => {
  const needle = toWords(query);
  if (!needle) return [];

  return getVisibleSettingsSections(ctx, clientTerm, pages)
    .flatMap((section) => section.pages)
    .flatMap((page) => {
      const hits = (page.settings ?? []).filter((label) =>
        matchesWordStart(label, needle),
      );
      const pageMatches =
        matchesWordStart(page.label, needle) ||
        (page.keywords ?? []).some((keyword) =>
          matchesWordStart(keyword, needle),
        );
      if (!pageMatches && hits.length === 0) return [];
      return [{ page, hits: hits.slice(0, MAX_SETTING_HITS) }];
    });
};
